if (!customElements.get('bv-smart-search')) {
  class BvSmartSearch extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.lifecycle = new AbortController();
      const opts = { signal: this.lifecycle.signal };
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('.bv-search__panel');
      this.results = this.querySelector('[data-results]');
      this.defaults = this.querySelector('[data-default]');
      this.discoveryList = this.defaults.querySelector('ul');
      this.discoveryPool = Array.from(this.querySelector('[data-discovery-pool]').content.children);
      this.lastDiscovery = new Set();
      this.status = this.querySelector('[data-status]');
      this.all = this.querySelector('[data-all]');
      this.form = this.querySelector('form');
      this.input.addEventListener('focus', () => { this.open(); this.search(); }, opts);
      this.input.addEventListener('pointerdown', () => {
        if (!this.panel.hidden && !this.input.value.trim()) { this.refreshDiscovery(); this.resize(); }
      }, opts);
      this.input.addEventListener('click', () => {
        if (this.panel.hidden) { this.open(); this.search(); }
      }, opts);
      this.input.addEventListener('input', () => { this.open(); this.search(); }, opts);
      this.form.addEventListener('submit', e => {
        if (!this.input.value.trim()) {
          e.preventDefault();
          const wasOpen = !this.panel.hidden;
          this.input.focus(); this.open(); this.search();
          if (wasOpen) this.refreshDiscovery();
          this.resize();
        }
      }, opts);
      this.querySelector('[data-close]').addEventListener('click', () => { this.input.focus(); this.close(); }, opts);
      this.querySelectorAll('[data-query]').forEach(link => link.addEventListener('click', e => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        e.preventDefault(); this.input.value = link.dataset.query; this.input.focus(); this.open(); this.search();
      }, opts));
      this.addEventListener('keydown', e => {
        if (e.key === 'Escape') { e.preventDefault(); this.input.focus(); this.close(); }
        if (e.key === 'ArrowDown' && e.target === this.input && !this.panel.hidden) {
          const first = this.panel.querySelector('[data-results]:not([hidden]) a') || this.panel.querySelector('[data-query]');
          if (first) { e.preventDefault(); first.focus(); }
        }
      }, opts);
      document.addEventListener('pointerdown', e => { if (!this.contains(e.target)) this.close(); }, opts);
      // Safari can blur the input without focusing the clicked link. Keep the
      // panel mounted until that native link click has completed.
      this.addEventListener('focusout', e => {
        if (e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
      }, opts);
      window.addEventListener('resize', () => this.resize(), opts);
      window.visualViewport?.addEventListener('resize', () => this.resize(), opts);
      this.examples = this.dataset.examples.split('|');
      this.exampleIndex = 0;
      this.rotation = setInterval(() => {
        if (!this.input.value && !this.contains(document.activeElement) && !document.hidden && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.exampleIndex = (this.exampleIndex + 1) % this.examples.length;
          this.input.placeholder = this.examples[this.exampleIndex];
        }
      }, 3600);
    }
    disconnectedCallback() {
      this.lifecycle?.abort(); this.request?.abort(); clearTimeout(this.debounce); clearInterval(this.rotation); this.bound = false;
    }
    resize() {
      const view = window.visualViewport;
      const bottom = view ? view.height + view.offsetTop : window.innerHeight;
      const available = Math.max(100, bottom - this.panel.getBoundingClientRect().top - 16);
      this.style.setProperty('--bv-panel-height', Math.min(580, available) + 'px');
    }
    refreshDiscovery() {
      const shuffled = this.discoveryPool.slice();
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      // Prefer products that were not shown in the previous group.
      const fresh = shuffled.filter(item => !this.lastDiscovery.has(item.dataset.productId));
      const previous = shuffled.filter(item => this.lastDiscovery.has(item.dataset.productId));
      const selected = [...fresh, ...previous].slice(0, 3);
      if (!selected.length) return;
      this.lastDiscovery = new Set(selected.map(item => item.dataset.productId));
      this.discoveryList.replaceChildren(...selected.map(item => item.cloneNode(true)));
    }
    open() {
      if (this.panel.hidden && !this.input.value.trim()) this.refreshDiscovery();
      this.panel.hidden = false; this.resize();
    }
    close() { this.panel.hidden = true; clearTimeout(this.debounce); this.request?.abort(); this.results.removeAttribute('aria-busy'); }
    search() {
      clearTimeout(this.debounce); this.request?.abort();
      this.results.removeAttribute('aria-busy');
      const query = this.input.value.trim();
      this.toggleAttribute('data-has-query', !!query);
      this.status.textContent = '';
      this.all.hidden = !query;
      const url = new URL(this.form.action);
      url.searchParams.set('q', query); url.searchParams.set('type', 'product'); url.searchParams.set('options[prefix]', 'last');
      this.all.href = url.href;
      this.defaults.hidden = !!query;
      this.results.hidden = true;
      if (!query) return;
      if (query.length < 2) { this.status.textContent = this.dataset.short; return; }
      this.status.textContent = this.dataset.loading;
      this.debounce = setTimeout(() => this.fetchResults(query), 250);
    }
    async fetchResults(query) {
      const controller = new AbortController(); this.request = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);
      this.results.setAttribute('aria-busy', 'true');
      const url = new URL(this.dataset.endpoint, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'bv-search-results');
      url.searchParams.set('resources[type]', 'product,collection');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      try {
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = doc.querySelector('[data-bv-response]');
        if (!content) throw new Error('Missing search response');
        if (this.request !== controller || this.input.value.trim() !== query || this.panel.hidden) return;
        this.results.replaceChildren(content); this.results.hidden = false;
        this.status.textContent = this.dataset.count.replace('[count]', content.dataset.resultCount);
        this.resize();
      } catch (error) {
        if (this.request !== controller || this.input.value.trim() !== query || this.panel.hidden) return;
        this.status.textContent = this.dataset.error;
      } finally {
        clearTimeout(timeout);
        if (this.request === controller) this.results.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('bv-smart-search', BvSmartSearch);
}
